/**
 * Build the per-slot indicator SIGNALS for the Scheduler List View by MERGING
 * the two independent per-slot data sources that previously produced
 * duplicate amber conflict triangles:
 *
 *   1. The auto-vote ENGINE reason (`voteAutoReasonBySlotId[slotId]`) —
 *      present only when the engine acted on the slot; carries deep links,
 *      `+N more` overflow, Restore/Keep-cleared urgent controls, etc.
 *   2. The calendar-OVERLAY records (from `buildOverlayRecords`) — the voter's
 *      own linked-calendar conflicts, free-time marks, and finalized sessions.
 *      Always present when the user has linked calendars, regardless of
 *      auto-vote.
 *
 * The merge produces at most ONE signal per `kind` (conflict,
 * preferred-align, feasible-align, no-group-match) — each a SYNTHETIC reason
 * consumable directly by `<SlotIndicator reason=… voteAutoSource=… />`.
 * `<SlotIndicator>` is reused as-is, so all of its machinery (enrichment,
 * a11y, deep links, `+N more`, Restore/Confirm) keeps working — we just feed
 * it merged data and render one indicator per signal.
 *
 * Pure + unit-tested. The caller pre-filters `overlayRecords` to those that
 * overlap the slot; this util operates on the records as given and needs no
 * slot times of its own.
 *
 * @param {object} opts
 * @param {object|null} [opts.reason]        engine `voteAutoReasonBySlotId[slotId]` or null
 * @param {string|null} [opts.voteAutoSource] `voteAutoSourceBySlotId[slotId]` or null
 * @param {Array} [opts.overlayRecords]       the overlay records overlapping this slot
 * @returns {Array<{key: string, kind: string, reason: object, voteAutoSource: string|null}>}
 *   ordered: conflict, preferred-align, feasible-align, no-group-match
 */
export function buildSlotIndicatorSignals({ reason, voteAutoSource, overlayRecords } = {}) {
  const records = Array.isArray(overlayRecords) ? overlayRecords : [];
  const signals = [];

  const conflict = buildConflictSignal(reason, voteAutoSource, records);
  if (conflict) signals.push(conflict);

  const preferred = buildAlignmentSignal("preferred-align", reason, records);
  if (preferred) signals.push(preferred);

  const feasible = buildAlignmentSignal("feasible-align", reason, records);
  if (feasible) signals.push(feasible);

  // no-group-match is engine-only — never sourced from overlay.
  if (reason && reason.kind === "no-group-match") {
    signals.push({
      key: "no-group-match",
      kind: "no-group-match",
      reason,
      voteAutoSource: null,
      fromEngine: true,
    });
  }

  return signals;
}

// ---------------------------------------------------------------------------
// Conflict signal
// ---------------------------------------------------------------------------

/**
 * Merge conflict rows from the engine reason AND overlay records into a single
 * synthetic conflict reason. Rows are deduped; engine rows win. Overlay-appended
 * refs are folded into `acknowledgedRefIds` so they can NEVER push the indicator
 * into the urgent (red) state — overlay-only conflicts were not engine-cleared
 * (see AGENTS.md privacy/guardrail model: preserve today's behavior exactly).
 */
function buildConflictSignal(reason, voteAutoSource, records) {
  const engineIsConflict = isEngineConflictReason(reason);
  const engineEvents = engineIsConflict && Array.isArray(reason.events) ? reason.events : [];
  const engineBusy =
    engineIsConflict && Array.isArray(reason.busyWindows) ? reason.busyWindows : [];

  const seenEventKeys = new Set();
  const seenBusyKeys = new Set();
  const mergedEvents = [];
  const mergedBusy = [];
  // Ref ids of OVERLAY rows we appended (pre-acknowledged below).
  const appendedOverlayRefIds = [];

  // Engine rows first — they win on dedupe.
  for (const e of engineEvents) {
    const key = eventKey(e);
    if (!key || !seenEventKeys.has(key)) {
      if (key) seenEventKeys.add(key);
      mergedEvents.push(e);
    }
  }
  for (const b of engineBusy) {
    const key = busyKey(b);
    if (!key || !seenBusyKeys.has(key)) {
      if (key) seenBusyKeys.add(key);
      mergedBusy.push(b);
    }
  }

  // Overlay conflicts — calendar events and finalized sessions.
  for (const r of records) {
    if (!r || r.kind !== "conflict") continue;
    if (r.sourceSchedulerId) {
      // Finalized session -> busy window.
      const key = r.sourceSchedulerId;
      if (seenBusyKeys.has(key)) continue;
      seenBusyKeys.add(key);
      mergedBusy.push({
        sourceSchedulerId: r.sourceSchedulerId,
        sourceSchedulerLabel: r.title || "Scheduled session",
        startUtc: r.startUtc,
        endUtc: r.endUtc,
      });
      appendedOverlayRefIds.push(key);
    } else {
      // Linked-calendar event -> event row. Deliberately OMIT provider/calendarId
      // so SlotIndicator does NOT network-enrich an overlay-sourced row (its
      // enrichment hook only fires when provider+calendarId+eventId are all
      // present). Reasons: (1) the deep link is keyed on eventId alone, so the
      // link works regardless; (2) the engine reason — when it flags the same
      // conflict — keeps its OWN enrichment, so auto-vote conflicts still show
      // titles; (3) enriching every overlay event would fire a title fetch that
      // 404s for any event a test's mock GCal server didn't seed (the old
      // VoterSlotOverlayBadges never fetched either). Do NOT re-add calendarId
      // here without making every overlay event enrichable in the test harness.
      const evt = {
        eventId: r.eventId,
        startUtc: r.startUtc,
        endUtc: r.endUtc,
        title: r.title,
      };
      const key = eventKey(evt);
      if (key && seenEventKeys.has(key)) continue; // engine duplicate -> drop
      if (key) seenEventKeys.add(key);
      mergedEvents.push(evt);
      if (r.eventId) appendedOverlayRefIds.push(r.eventId);
    }
  }

  // Legacy Chunk-C engine shape: no events/busyWindows but humanLabel + refId.
  // SlotIndicator synthesizes a row from these — keep them on the synthetic.
  const hasEngineLegacy =
    engineIsConflict &&
    !reason.kind &&
    engineEvents.length === 0 &&
    engineBusy.length === 0 &&
    reason.humanLabel != null;

  if (mergedEvents.length === 0 && mergedBusy.length === 0 && !hasEngineLegacy) {
    return null;
  }

  // Pre-acknowledge every overlay-appended ref so overlay-only conflicts can't
  // fire the urgent path (only genuinely engine-unacknowledged refs can).
  const engineAck =
    engineIsConflict && Array.isArray(reason.acknowledgedRefIds) ? reason.acknowledgedRefIds : [];
  const ackSet = new Set([...engineAck, ...appendedOverlayRefIds]);

  const synthetic = {
    kind: "conflict",
    events: mergedEvents,
    busyWindows: mergedBusy,
    acknowledgedRefIds: Array.from(ackSet),
  };
  if (reason) {
    if (reason.previousManualVote != null) synthetic.previousManualVote = reason.previousManualVote;
    if (reason.rsvpCascade != null) synthetic.rsvpCascade = reason.rsvpCascade;
    if (reason.humanLabel != null) synthetic.humanLabel = reason.humanLabel;
    if (reason.refId != null) synthetic.refId = reason.refId;
    if (reason.type != null) synthetic.type = reason.type;
  }
  return {
    key: "conflict",
    kind: "conflict",
    reason: synthetic,
    voteAutoSource: voteAutoSource ?? null,
    // The engine reason contributed to this signal — keep the legacy default
    // testid (`slot-indicator-<slotId>`) so existing e2e locators still resolve.
    // Overlay-ONLY signals (fromEngine=false) get the kind-suffixed testid.
    fromEngine: engineIsConflict,
  };
}

/**
 * An engine reason counts as a conflict when it either carries
 * `kind === "conflict"` (post-E1) or has the legacy no-kind conflict shape
 * (events/busyWindows/humanLabel present but no top-level kind), mirroring the
 * dispatch `<SlotIndicator>` itself does.
 */
function isEngineConflictReason(reason) {
  if (!reason) return false;
  if (reason.kind === "conflict") return true;
  if (
    !reason.kind &&
    (Array.isArray(reason.events) ||
      Array.isArray(reason.busyWindows) ||
      reason.humanLabel != null)
  ) {
    return true;
  }
  return false;
}

// Dedupe on `eventId` ALONE — that is the identity the rest of the system uses
// (the engine's `acknowledgedRefIds`, `hasUnacknowledgedRefIds`, the popover deep
// links, and EventRow keys all key on bare `eventId`). The engine reason and the
// overlay record for the same calendar event can carry DIFFERENT `calendarId`
// shapes (the engine row may omit it), so a compound `provider:calendarId:eventId`
// key would fail to dedupe them — leaving a duplicate overlay row that fires a
// redundant title-enrichment fetch (and double-counts the conflict).
function eventKey(e) {
  return e?.eventId || null;
}

function busyKey(b) {
  return b?.sourceSchedulerId || null;
}

// ---------------------------------------------------------------------------
// Alignment signals (preferred-align / feasible-align)
// ---------------------------------------------------------------------------

/**
 * Build a synthetic alignment reason from the engine reason (preferred source)
 * falling back to the first overlay record of the same kind. The synthetic
 * carries either a deep-linkable `mark` (parsed from a mark record id) or an
 * `event` (from a linked-calendar free-time record) — both render as deep
 * links in `<SlotIndicator>`'s AlignmentBody.
 */
function buildAlignmentSignal(kind, reason, records) {
  const fromEngine = Boolean(reason && reason.kind === kind);
  const overlayForKind = records.filter((r) => r && r.kind === kind);
  if (!fromEngine && overlayForKind.length === 0) return null;

  let mark = null;
  let event = null;
  if (fromEngine) {
    mark = reason.mark || null;
    event = reason.event || null;
  }
  if (!mark && !event && overlayForKind.length > 0) {
    const r = overlayForKind[0];
    if (r.eventId != null) {
      // Linked-calendar free-time record.
      event = {
        provider: r.provider,
        calendarId: r.calendarId,
        eventId: r.eventId,
        startUtc: r.startUtc,
        endUtc: r.endUtc,
      };
    } else {
      // Availability-mark record (id shaped "mark:<markId>:<iso>").
      mark = { markId: parseMarkId(r.id), title: r.title || null };
    }
  }

  const synthetic = { kind };
  if (mark) synthetic.mark = mark;
  if (event) synthetic.event = event;
  if (!mark && !event && reason?.humanLabel != null) synthetic.humanLabel = reason.humanLabel;
  // Provenance flag consumed by SlotIndicator's AlignmentBody: only an
  // engine-driven alignment is "Auto Preferred/Feasible" (the engine actually
  // derived a vote). An overlay-only alignment is just the user's calendar
  // matching this time — it must NOT claim "Auto" (no vote was cast).
  synthetic.fromEngine = fromEngine;
  return { key: kind, kind, reason: synthetic, voteAutoSource: null, fromEngine };
}

/**
 * Extract the markId from an overlay record id shaped "mark:<markId>:<iso>".
 * markIds are Firestore doc ids (no colons), so taking the second segment is
 * safe — a colon in the markId would truncate, but that never occurs in practice.
 */
function parseMarkId(id) {
  if (typeof id !== "string" || !id.startsWith("mark:")) return null;
  const parts = id.split(":");
  // ["mark", markId, ...iso]
  return parts[1] || null;
}
